import { useMutation } from '@tanstack/react-query';
import download from 'downloadjs';
import { useIntl } from 'react-intl';
import { endpoints } from '@rossum/api-client';
import { Group } from '@rossum/api-client/groups';
import { User, UsersListQuery } from '@rossum/api-client/users';
import { api } from '../../../lib/apiClient';
import { buildCsvBlob } from '../../../lib/csv';
import { notify } from '../../toaster';

type UserExportRow = Pick<
  User,
  | 'id'
  | 'username'
  | 'lastName'
  | 'isActive'
  | 'dateJoined'
  | 'lastLogin'
  | 'deleted'
> & {
  email: string;
  firstName: string;
  role: string;
  authType: 'password' | 'sso';
};

const CSV_HEADERS = [
  'ID',
  'Email',
  'Username',
  'First Name',
  'Last Name',
  'Role',
  'Active',
  'Log in with',
  'Date Joined',
  'Last Login',
  'Deleted',
];

const formatRoleName = (role: string): string =>
  role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getUserRole = (userGroupUrls: string[], groups: Group[]): string => {
  const userGroup = groups.find(group => userGroupUrls.includes(group.url));
  return userGroup ? formatRoleName(userGroup.name) : '';
};

const userToExportRow = (user: User, groups: Group[]): UserExportRow => ({
  id: user.id,
  email: user.email ?? '',
  username: user.username,
  firstName: user.firstName ?? '',
  lastName: user.lastName,
  role: getUserRole(user.groups, groups),
  isActive: user.isActive,
  authType: user.authType,
  dateJoined: user.dateJoined,
  lastLogin: user.lastLogin,
  deleted: user.deleted,
});

const userToRowValues = (user: UserExportRow): unknown[] => [
  user.id,
  user.email,
  user.username,
  user.firstName,
  user.lastName,
  user.role,
  user.isActive ? 'Yes' : 'No',
  user.authType === 'password' ? 'Password' : 'SSO',
  user.dateJoined,
  user.lastLogin ?? '',
  user.deleted ? 'Yes' : 'No',
];

const fetchAllUsers = async (
  query: Omit<UsersListQuery, 'page' | 'pageSize'>
): Promise<UserExportRow[]> => {
  const [users, groupsResponse] = await Promise.all([
    api.unpaginatedRequest(endpoints.users.list)(query),
    api.request(endpoints.groups.list()),
  ]);

  const groups = groupsResponse.results;

  return users.map(user => userToExportRow(user, groups));
};

export const useExportUsers = () => {
  const intl = useIntl();
  return useMutation({
    mutationFn: (query: Omit<UsersListQuery, 'page' | 'pageSize'>) =>
      fetchAllUsers(query),
    onSuccess: users => {
      const blob = buildCsvBlob(users, CSV_HEADERS, userToRowValues);
      const timestamp = new Date().toISOString().split('T')[0];
      download(blob, `users-export-${timestamp}.csv`, 'text/csv');
      notify.default({
        title: intl.formatMessage({ id: 'features.users.export.success' }),
      });
    },
    onError: () => {
      notify.error({
        title: intl.formatMessage({ id: 'features.users.export.error' }),
      });
    },
  });
};
